import axios from 'axios';

import {
  CodeAgentCostEstimate,
  CodeAgentProvider,
  CodeAgentTask,
  CodeAgentTier,
  CodeAgentUsage,
} from '../../entities/code-agent-task';
import { auth } from '../firebase/firebase';
import { API_BASE_URL } from './index';

const getHeaders = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

export const codeAgentErrorMessage = (err: unknown): string | undefined =>
  axios.isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;

export interface CreateCodeAgentTaskInput {
  prompt: string;
  repoUrl?: string; // omit for a fresh repo
  branch?: string; // only meaningful alongside repoUrl — omit to use that repo's default branch
  aiProvider?: CodeAgentProvider; // omit for mFlow's hosted key
  standaloneTaskId?: string; // links this run to a Standalone project task
  tier?: CodeAgentTier; // omit for BE's default ('mid')
}

export const createCodeAgentTask = async (input: CreateCodeAgentTaskInput): Promise<CodeAgentTask> => {
  const headers = await getHeaders();
  const { data } = await axios.post<CodeAgentTask>(`${API_BASE_URL}/api/code-agent/tasks`, input, { headers });
  return data;
};

export const listCodeAgentTasks = async (): Promise<CodeAgentTask[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ tasks: CodeAgentTask[] }>(`${API_BASE_URL}/api/code-agent/tasks`, { headers });
  return data.tasks;
};

export const getCodeAgentTask = async (taskId: string): Promise<CodeAgentTask> => {
  const headers = await getHeaders();
  const { data } = await axios.get<CodeAgentTask>(`${API_BASE_URL}/api/code-agent/tasks/${taskId}`, { headers });
  return data;
};

// null limit/remaining = unlimited (BYOK, or the scale plan) — hosted-path only, a BYOK task
// never touches this.
export const getCodeAgentUsage = async (): Promise<CodeAgentUsage> => {
  const headers = await getHeaders();
  const { data } = await axios.get<CodeAgentUsage>(`${API_BASE_URL}/api/code-agent/usage`, { headers });
  return data;
};

// `stopped: false` means the task had already finished before the request landed — a race, not an
// error; the task's status settling to 'cancelled' (or whatever it already was) arrives via the
// existing codeAgentTaskProgress WS event, not this response.
export const stopCodeAgentTask = async (taskId: string): Promise<{ stopped: boolean }> => {
  const headers = await getHeaders();
  const { data } = await axios.post<{ stopped: boolean }>(
    `${API_BASE_URL}/api/code-agent/tasks/${taskId}/stop`,
    {},
    { headers },
  );
  return data;
};

// MF-50 — resumes a task whose PR got review feedback. Always returns a NEW task record
// (`resumedFromTaskId` points back at `taskId`), never a mutation of the original — the caller
// should navigate to/insert the response, not patch the original task in place.
export const resumeCodeAgentTask = async (taskId: string, additionalContext?: string): Promise<CodeAgentTask> => {
  const headers = await getHeaders();
  const { data } = await axios.post<CodeAgentTask>(
    `${API_BASE_URL}/api/code-agent/tasks/${taskId}/resume`,
    additionalContext ? { additionalContext } : {},
    { headers },
  );
  return data;
};

// MF-50 §2b — on-demand refresh of PR review state, instead of waiting on the 15-minute scheduled
// poll. Optional/additive: everything MF-50 needs already works off the scheduled poll alone.
export const checkPrReviewNow = async (taskId: string): Promise<CodeAgentTask> => {
  const headers = await getHeaders();
  const { data } = await axios.post<CodeAgentTask>(
    `${API_BASE_URL}/api/code-agent/tasks/${taskId}/check-pr-review`,
    {},
    { headers },
  );
  return data;
};

// Model cost only (LLM API billing, never infra) — one estimate per provider. `basis:
// 'fallback-assumption'` means no completed task exists yet for that provider (a rough
// placeholder); 'historical-average' reflects real past task costs.
export const estimateCodeAgentCost = async (prompt: string): Promise<CodeAgentCostEstimate[]> => {
  const headers = await getHeaders();
  const { data } = await axios.post<{ estimates: CodeAgentCostEstimate[] }>(
    `${API_BASE_URL}/api/code-agent/estimate`,
    { prompt },
    { headers },
  );
  return data.estimates;
};
